'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';
import TrailingLink from '@/components/atoms/TrailingLink';
import SearchIcon from '@/components/atoms/SearchIcon';
import { navigationData } from '@/lib/navigationData';
import styles from './SearchModal.module.scss';

interface SearchResult {
  title: string;
  path: string;
  category: string;
  level: number;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const pathname = usePathname();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [mounted, setMounted] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Close modal only when route actually changes (skip initial mount)
  const initialPathRef = useRef(pathname);
  useEffect(() => {
    if (initialPathRef.current !== pathname) {
      onClose();
      initialPathRef.current = pathname;
    }
  }, [pathname, onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Focus input after a short delay
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Build search index from navigation data
  const buildSearchIndex = useCallback(() => {
    const results: SearchResult[] = [];

    navigationData.forEach((level1) => {
      // Add level 1 items
      if (level1.href) {
        results.push({
          title: level1.label,
          path: level1.href,
          category: 'メインセクション',
          level: 1,
        });
      }

      // Add level 2 items
      if (level1.children) {
        level1.children.forEach((level2) => {
          if (level2.href) {
            results.push({
              title: level2.label,
              path: level2.href,
              category: level1.label,
              level: 2,
            });
          }

          // Add level 3 items
          if (level2.children) {
            level2.children.forEach((level3) => {
              if (level3.href) {
                results.push({
                  title: level3.label,
                  path: level3.href,
                  category: `${level1.label} > ${level2.label}`,
                  level: 3,
                });
              }
            });
          }
        });
      }
    });

    return results;
  }, []);

  // Search handler
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const searchIndex = buildSearchIndex();
    const normalizedQuery = query.toLowerCase();

    const filtered = searchIndex.filter((item) => {
      const normalizedTitle = item.title.toLowerCase();
      const normalizedCategory = item.category.toLowerCase();
      
      return (
        normalizedTitle.includes(normalizedQuery) ||
        normalizedCategory.includes(normalizedQuery)
      );
    });

    // Sort by relevance (exact matches first, then by level)
    filtered.sort((a, b) => {
      const aExact = a.title.toLowerCase() === normalizedQuery;
      const bExact = b.title.toLowerCase() === normalizedQuery;
      
      if (aExact && !bExact) { return -1; }
      if (!aExact && bExact) { return 1; }
      
      return a.level - b.level;
    });

    setSearchResults(filtered.slice(0, 10)); // Limit to 10 results
  }, [buildSearchIndex]);

  if (!mounted || !isOpen) { return null; }

  // Ensure we're on the client side before using createPortal
  if (typeof window === 'undefined' || !document.body) {
    return null;
  }

  return createPortal(
    <div 
      className={styles.searchModal} 
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-label="検索モーダル"
    >
      <div 
        className={styles.modalContent} 
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="document"
      >
        <div className={styles.searchHeader}>
          <div className={styles.searchInputWrapper}>
            <SearchIcon className={styles.searchIcon} />
            <input
              id="search-modal-input"
              name="search"
              type="search"
              className={styles.searchInput}
              placeholder="ページを検索..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              ref={searchInputRef}
              aria-label="サイト内検索"
            />
          </div>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="検索を閉じる"
          >
            <span className={styles.closeIcon}>×</span>
          </button>
        </div>

        {searchQuery && (
          <div className={styles.searchResults}>
            {searchResults.length > 0 ? (
              <>
                <div className={styles.resultCount}>
                  {searchResults.length}件の結果
                </div>
                <ul className={styles.resultsList}>
                  {searchResults.map((result, index) => (
                    <li key={`${result.path}-${index}`}>
                      <TrailingLink
                        href={result.path}
                        className={styles.resultLink}
                        onClick={onClose}
                      >
                        <div className={styles.resultTitle}>{result.title}</div>
                        <div className={styles.resultCategory}>{result.category}</div>
                      </TrailingLink>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className={styles.noResults}>
                「{searchQuery}」に一致する結果が見つかりませんでした
              </div>
            )}
          </div>
        )}

        {!searchQuery && (
          <div className={styles.searchHelp}>
            <p>検索キーワードを入力してください</p>
            <div className={styles.shortcuts}>
              <kbd>ESC</kbd> 閉じる
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default SearchModal;